<template>
  <!-- Changed from svg to canvas, because of a bug in echarts:
    https://github.com/apache/incubator-echarts/issues/9565
  -->
  <div>
    <v-dialog
      v-model="markPointDialog"
      max-width="250"
    >
      <v-card>
        <v-card-text v-if="markPointDialog">
          <div :style="`color: ${markPointDialogParams.color}`" class="text-h6 font-weight-bold pt-2">{{ channelKeys[markPointDialogParams.seriesIndex] }}</div>
          <div class="text-body-2 pb-3">{{ data[channelKeys[markPointDialogParams.seriesIndex]].datapoints_dates[markPointDialogParams.value - 1] ? data[channelKeys[markPointDialogParams.seriesIndex]].datapoints_dates[markPointDialogParams.value - 1] : '' }}</div>
          <div class="text-body-1">
            <span style="display: inline-block; width: 120px;">{{ this.xName }}</span>
            <span class="font-weight-black">{{ formatValue(data[channelKeys[markPointDialogParams.seriesIndex]].datapoints[markPointDialogParams.value - 1].x) }}</span>
          </div>
          <div class="text-body-1 pt-1">
            <span style="display: inline-block; width: 120px;">{{ this.yName }}</span>
            <span class="font-weight-black">{{ formatValue(data[channelKeys[markPointDialogParams.seriesIndex]].datapoints[markPointDialogParams.value - 1].y) }}</span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <div :style="{ minHeight: chartHeight + 'px' }"><v-chart v-if="showChart" :option="options" :id="chartId" :ref="chartId" :init-options="{renderer: 'canvas'}" autoresize :style="{ height: chartHeight + 'px', width: '100%' }" @click="clickedChart" @legendselectchanged="onLegendSelectChanged" @legendselectall="onLegendSelectAll" @legendinverseselect="onLegendInverseSelect"/></div>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/markLine'
import 'echarts/lib/component/markArea'
import 'echarts/lib/component/markPoint'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/title'
import 'echarts/lib/component/legendScroll'
import 'echarts/lib/component/dataZoom'
// import 'zrender/lib/svg/svg'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
import moment from 'moment'
import { regionsStrictlyIncreasing } from '@/utils/regions.js'
import { significantMantissa } from '@/utils/numberFormat.js'

export default {
  data () {
    return {
      currentDatapointsIndex: 0,
      markPointDialog: false,
      markPointDialogParams: {},
      browseDatapoints: false,
      showPointNumbers: false,
      selectedSeries: {},
      showChart: false
    }
  },
  computed: {
    tooltip () {
      const cross = {
        trigger: 'item',
        showContent: this.showTooltips,
        axisPointer: {
          type: 'cross',
          label: {
            show: true,
            formatter: this.formatEchartsValue
          }
        },
        formatter: (params) => {
          // seriesIndex is checked by type, not truthiness: the first channel is series 0
          if (params && params.value && typeof params.seriesIndex === 'number') {
            const channelData = this.data[this.channelKeys[params.seriesIndex]]
            if (channelData && channelData.datapoints_dates) {
              const date = channelData.datapoints_dates[params.value - 1]
              return this.formatDateX(date)
            }
            return ''
          } else {
            return ''
          }
        }
      }
      const detail = {
        trigger: 'axis',
        formatter: (params) => {
          let lineStylesObj
          if (this.lineStyles) {
            lineStylesObj = JSON.parse(this.lineStyles)
          }
          var tooltip = `<b style="font-size: 16px;">${this.xAxisTypeIsDate ? this.formatDateX(params[0].value[0]) : params[0].value[0]}</b><br><br>`
          const visibleParams = []
          for (let index = params.length - 1; index >= 0; index--) {
            const param = params[index]
            const seriesStyle = lineStylesObj && param.seriesName in lineStylesObj ? lineStylesObj[param.seriesName] : null
            if (seriesStyle && seriesStyle.hideInTooltip === true) {
              continue
            }
            const color = (param.color && param.color.colorStops && param.color.colorStops.length > 1 && param.color.colorStops[1] && param.color.colorStops[1].color)
              ? param.color.colorStops[1].color
              : (param.color || '#999999')
            const rawValue = param.value[1]
            const valueHtml = seriesStyle && seriesStyle.tooltipPlusMinus === true
              ? `± ${this.formatValue(rawValue / 2)}`
              : `${this.formatValue(rawValue)}${this.sumOneHundred ? '%' : ''}`
            tooltip += `
              <b style="color: ${color}">▶</b>
              ${this.getDisplayName(param.seriesName)}:
              <span style="color: lightblue; font-size: 16px;">${valueHtml}</span>
              <br>
            `
            visibleParams.push(param)
          }
          if (this.showDifference && visibleParams.length === 2) {
            const ordered = visibleParams.slice().sort((a, b) => a.seriesIndex - b.seriesIndex)
            const baseline = ordered[1].value[1]
            const diff = ordered[0].value[1] - baseline
            let pctHtml = ''
            if (typeof baseline === 'number' && baseline !== 0) {
              const pct = diff / baseline * 100
              pctHtml = ` <span style="color: #bbb;">(${pct >= 0 ? '+' : '−'}${this.formatValue(Math.abs(pct))}%)</span>`
            }
            tooltip += `<hr style="border: none; border-top: 1px solid #888; margin: 6px 0;">`
            tooltip += `
              ${this.differenceLabel}:
              <span style="color: lightblue; font-size: 16px;">${this.formatValue(diff)}${this.sumOneHundred ? '%' : ''}</span>${pctHtml}
              <br>
            `
          }
          return tooltip
        }
      }
      if (this.detailedTooltip) {
        return detail
      } else {
        return cross
      }
    },
    gridBottom () {
      if (this.showDataZoom) {
        return this.xName ? 70 : Math.max(this.gridBottomMargin, 40) + 30
      }
      if (this.xName) {
        return this.gridBottomMargin
      }
      return 0
    },
    gridRight () {
      if (this.showLegend && this.legendOrient === 'vertical' && this.channelKeys.length > 0) {
        return Math.max(this.$i18n.locale === 'es' || this.$i18n.locale === 'pt' ? 170 : 150, this.longestLabelLength * 10)
      }
      return 50
    },
    zoomRight () {
      // Keep the slider's right edge aligned with the plot area (not under the
      // vertical legend), while reserving at least 70px so the right handle's
      // date-label tooltip isn't clipped at the chart edge.
      return Math.max(this.gridRight, 70)
    },
    chartHeight () {
      // Reserve extra vertical room when the legend is stacked under the chart, so the
      // chart, the legend and the zoom bar all fit without the bar being clipped. Only when
      // there is a legend to stack: legendBottom also says "the key does not stand at the top
      // of the plot", which is true of a chart that has no key at all, and a chart with none
      // should not be paying for the space one would have taken.
      return this.legendBottom && this.showLegend ? 430 : 350
    },
    legendPosition () {
      // legendBottom lays the (horizontal) legend out below the chart instead of the
      // default top-right corner, so it doesn't steal horizontal width in narrow columns.
      // When a zoom bar is present the legend sits between the chart and the bar (the bar
      // is lifted off the bottom edge so its handle labels aren't clipped), with breathing
      // room on either side.
      if (this.legendBottom) {
        return { bottom: this.showDataZoom ? 52 : 0, left: 'center' }
      }
      return { right: 0, top: 0 }
    },
    currentDatapoints () {
      if (this.hideDatapoints) {
        return {}
      }
      let dataPoints = {}
      this.channelKeys.forEach(channel => {
        if (this.data[channel].datapoints && this.currentDatapointsIndex >= 0 && this.browseDatapoints) {
          dataPoints[channel] = [this.data[channel].datapoints[this.currentDatapointsIndex]]
        } else if (this.data[channel].datapoints) {
          dataPoints[channel] = this.data[channel].datapoints
        }
      })
      return dataPoints
    },
    options () {
      let options = {
        title: {
          text: this.titleString,
          show: this.hideTitle && this.titleString && this.titleString !== ''
        },
        animation: this.animation,
        animationDuration: 250,
        tooltip: this.tooltip,
        dataZoom: [
          {
            type: 'slider',
            start: 0,
            end: 100,
            right: this.zoomRight,
            bottom: this.legendBottom ? 12 : null,
            filterMode: this.filterMode,
            show: this.showDataZoom,
            labelFormatter: this.dataZoomLabelFormatter
          }
        ],
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.dataView'),
              lang: [this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.dataView'), this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.close'), this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.$attrs.exportImageName || 'ExportedImage',
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        legend: {
          show: this.showLegend && this.channelKeys.length > 0,
          data: this.legendKeys,
          type: 'scroll',
          orient: this.legendOrient,
          ...this.legendPosition,
          selector: this.legendOrient === 'vertical' && !this.showSelector && this.channelKeys.length > 1 ? [
            {
              type: 'all',
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.selectAll')
            },
            {
              type: 'inverse',
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.invert')
            }
          ] : null,
          selectedMode: !this.showSelector,
          formatter: (name) => this.getDisplayName(name)
        },
        grid: {
          top: this.legendBottom ? 10 : (this.legendOrient === 'vertical' ? 10 : 50),
          left: 50,
          right: this.gridRight,
          bottom: this.legendBottom && this.showLegend ? this.gridBottom + 52 : this.gridBottom,
          containLabel: true
        },
        xAxis: {
          type: this.xAxisTypeIsCategory ? 'category' : 'value',
          name: this.xName,
          nameLocation: 'center',
          nameGap: this.gridBottomMargin,
          min: this.xAxisTypeIsCategory ? null : 0,
          max: this.xAxisTypeIsCategory ? null : this.axisMaxOptions.x,
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            show: !this.hideAxisValues,
            formatter: this.xAxisTypeIsCategory ? (this.xAxisTypeIsDate ? this.formatDateX : null) : this.formatValue,
            rotate: this.xAxisRotate ? this.xAxisRotate : null
          }
        },
        yAxis: {
          type: 'value',
          name: this.yName,
          nameLocation: 'middle',
          nameGap: 50,
          scale: this.scale,
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            show: !this.hideAxisValues,
            formatter: this.formatValue
          }
        },
        series: this.series
      }
      if (this.secondaryYName) {
        options.yAxis = [options.yAxis, {
          type: 'value',
          name: this.secondaryYName,
          nameLocation: 'middle',
          nameGap: 50,
          position: 'right',
          scale: this.scale,
          splitLine: {
            show: false
          },
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            show: !this.hideAxisValues,
            formatter: this.formatValue
          }
        }]
      }
      // Automatically scaling to visible data and fixed min/max are not compatible
      // https://echarts.apache.org/en/option.html#yAxis.scale
      if (!this.scale && !Array.isArray(options.yAxis)) {
        options.yAxis.min = this.showNegatives ? null : 0
        options.yAxis.max = this.axisMaxOptions.y
      }
      return options
    },
    series: function () {
      let lineStylesObj
      if (this.lineStyles) {
        lineStylesObj = JSON.parse(this.lineStyles)
      }
      let series = this.channelKeys.map(channel => {
        // A POINT THAT SAYS IT IS NOT A READING. Rides on the series itself — `mark` beside `x`,
        // `y` and `regions` — because it is a fact about that curve and not about the chart, and
        // because a caller that has a curve to hand has the point to hand with it. Appended to
        // whatever the browsed datapoints already put on the plot rather than replacing it, and
        // absent entirely when nothing asks for it: markPoint.data stays exactly the null it was.
        const browsed = this.currentDatapoints[channel] ? this.currentDatapoints[channel].map((datapoint, index) => {
          let value = this.currentDatapointsIndex + 1
          if (this.currentDatapoints[channel].length > 1) {
            if (datapoint.type === 'avg') {
              value = 'μ'
            } else {
              value = `${index + 1}`
            }
          }
          return {
            name: `${index}`,
            symbol: 'circle',
            symbolSize: this.showPointNumbers ? 14 : 3,
            value: value,
            xAxis: datapoint.x,
            yAxis: datapoint.y,
            itemStyle: {
              borderWidth: value === 'μ' ? 2 : 1,
              borderColor: 'black',
              color: this.colors && Array.isArray(this.colors)
                ? this.colors[this.channelKeys.indexOf(channel) % this.colors.length]
                : (this.colors && channel in this.colors ? this.colors[channel] : null)
            },
            label: {
              fontSize: this.showPointNumbers ? 8 : 0,
              fontWeight: 'bold',
              show: true
            }
          }
        }) : null
        const mark = this.data[channel].mark
        const markData = mark ? (browsed || []).concat([this.unknownPoint(mark)]) : browsed
        const seriesObj = {
          name: channel,
          type: lineStylesObj && channel in lineStylesObj && 'seriesType' in lineStylesObj[channel] ? lineStylesObj[channel].seriesType : 'line',
          smooth: lineStylesObj && channel in lineStylesObj && 'smooth' in lineStylesObj[channel] ? lineStylesObj[channel].smooth : this.smoothLines,
          seriesLayoutBy: 'row',
          symbol: lineStylesObj && channel in lineStylesObj && 'symbol' in lineStylesObj[channel] ? lineStylesObj[channel].symbol : 'none',
          symbolSize: lineStylesObj && channel in lineStylesObj && 'symbolSize' in lineStylesObj[channel] ? lineStylesObj[channel].symbolSize : null,
          data: this.data[channel].datapoints ? this.dataArray(this.mergeLine(this.data[channel].x, this.data[channel].y, this.data[channel].datapoints)) : this.dataArray(this.data[channel]),
          regions: this.data[channel].regions,
          lineStyle: {
            type: lineStylesObj && channel in lineStylesObj && 'type' in lineStylesObj[channel] ? lineStylesObj[channel].type : null,
            width: lineStylesObj && channel in lineStylesObj && 'width' in lineStylesObj[channel] ? lineStylesObj[channel].width : null
          },
          itemStyle: {
            color: this.colors && Array.isArray(this.colors)
              ? this.colors[this.channelKeys.indexOf(channel) % this.colors.length]
              : (this.colors && channel in this.colors ? this.colors[channel] : null)
          },
          markPoint: {
            data: markData
          }
        }
        if (lineStylesObj && channel in lineStylesObj && 'yAxisIndex' in lineStylesObj[channel]) {
          seriesObj.yAxisIndex = lineStylesObj[channel].yAxisIndex
        }
        if (lineStylesObj && channel in lineStylesObj && 'stack' in lineStylesObj[channel]) {
          seriesObj.stack = lineStylesObj[channel].stack
        }
        if (lineStylesObj && channel in lineStylesObj && 'areaStyle' in lineStylesObj[channel]) {
          seriesObj.areaStyle = lineStylesObj[channel].areaStyle
        }
        return seriesObj
      })
      if (this.showDiagonal) {
        series.push({
          name: 'Diagonal',
          id: 'diagonal',
          type: 'line',
          symbol: 'none',
          z: 1,
          data: [0, Math.min(this.axisMaxValues.x, this.axisMaxValues.y)],
          lineStyle: {
            color: 'lightgray',
            width: 1,
            type: 'dashed'
          }
        })
      }
      // Which series' four boundaries shade the plot, and whether any do. A chart of one curve
      // shades itself, as it always has; a chart of several shades nothing unless a caller has named
      // the series the zones belong to, because zones are a fact about ONE channel.
      const regionsKey = this.regionsChannel && this.channelKeys.indexOf(this.regionsChannel) !== -1
        ? this.regionsChannel
        : (this.channelKeys.length === 1 ? this.channelKeys[0] : null)
      const regionsSeries = regionsKey === null ? null : series[this.channelKeys.indexOf(regionsKey)]
      const regions = regionsSeries ? regionsSeries.regions : null
      if (regions && regions.length === 4 && regionsStrictlyIncreasing(regions)) {
        regionsSeries.markArea = {
          silent: true,
          data: [
            [
              {
                name: 'Threshold',
                xAxis: regions[0],
                itemStyle: {
                  color: 'rgba(213, 244, 254, 0.8)'
                },
                label: {
                  show: false,
                  color: 'white',
                  fontSize: 24,
                  textBorderType: 'solid',
                  // textBorderWidth: 3,
                  // textBorderColor: 'rgba(213, 244, 254, 1)',
                  textBorderWidth: 0.5,
                  textBorderColor: 'black',
                  position: 'inside',
                  rotate: '90',
                  padding: [0, 126, 0, 0]
                }
              },
              {
                xAxis: regions[1]
              }
            ],
            [
              {
                name: 'Max Marginal',
                xAxis: regions[1],
                itemStyle: {
                  color: 'rgba(235, 247, 225, 1)'
                },
                label: {
                  show: false,
                  color: 'white',
                  fontSize: 24,
                  textBorderType: 'solid',
                  // textBorderWidth: 3,
                  // textBorderColor: 'rgba(235, 247, 225, 1)',
                  textBorderWidth: 0.5,
                  textBorderColor: 'black',
                  position: 'inside',
                  rotate: '90',
                  padding: [0, 91, 0, 0]
                }
              },
              {
                xAxis: regions[2]
              }
            ],
            [
              {
                name: 'Max ROAS',
                xAxis: regions[2],
                itemStyle: {
                  color: 'rgba(255, 243, 215, 0.8)'
                },
                label: {
                  show: false,
                  color: 'white',
                  fontSize: 24,
                  textBorderType: 'solid',
                  // textBorderWidth: 3,
                  // textBorderColor: 'rgba(255, 243, 215, 1)',
                  textBorderWidth: 0.5,
                  textBorderColor: 'black',
                  position: 'inside',
                  rotate: '90',
                  padding: [0, 140, 0, 0]
                }
              },
              {
                xAxis: regions[3]
              }
            ],
            [
              {
                name: 'Diminishing Returns',
                xAxis: regions[3],
                itemStyle: {
                  color: 'rgba(255, 216, 219, 0.8)'
                },
                label: {
                  show: false,
                  color: 'white',
                  fontSize: 24,
                  textBorderType: 'solid',
                  // textBorderWidth: 3,
                  // textBorderColor: 'rgba(255, 216, 219, 1)',
                  textBorderWidth: 0.5,
                  textBorderColor: 'black',
                  position: 'inside',
                  rotate: '90',
                  padding: [0, 20, 0, 0]
                }
              },
              {}
            ]
          ]
        }
      }
      if (this.data && this.channelKeys) {
        for (let i = 0; i < this.channelKeys.length; i++) {
          const channelData = this.data[this.channelKeys[i]]
          if (channelData && channelData._epochBands && channelData._epochBands.length > 0 && series[i]) {
            const epochBands = channelData._epochBands
            series[i].markArea = {
              silent: true,
              z: 0,
              data: epochBands.map(band => [
                {
                  xAxis: band[0],
                  itemStyle: {
                    color: 'rgba(173, 216, 230, 0.35)',
                    borderColor: 'rgba(163, 193, 218, 0.9)',
                    borderWidth: 1
                  }
                },
                { xAxis: band[1] }
              ])
            }
          }
        }
      }
      return series
    },
    channelKeys: function () {
      if (this.only && this.only.length > 0) {
        return this.only
      } else {
        return Object.keys(this.data)
      }
    },
    legendKeys: function () {
      if (!this.lineStyles) {
        return this.channelKeys
      }
      const lineStylesObj = JSON.parse(this.lineStyles)
      return this.channelKeys
        .filter(channel => {
          return !(channel in lineStylesObj && lineStylesObj[channel].hideInLegend === true)
        })
        .map(channel => {
          if (channel in lineStylesObj && lineStylesObj[channel].legendIcon) {
            return { name: channel, icon: lineStylesObj[channel].legendIcon }
          }
          return channel
        })
    },
    visibleChannelKeys: function () {
      const visible = this.channelKeys.filter(key => this.selectedSeries[key] !== false)
      // Fall back to all channels if everything is deselected, so the axes
      // stay put on an empty chart
      return visible.length > 0 ? visible : this.channelKeys
    },
    visibleMaxValues: function () {
      return this.seriesMaxValues(this.visibleChannelKeys)
    },
    // What the axes are actually drawn to. Identical to the raw maxima unless sameAxisScale is on,
    // and then a single number on both: see the prop.
    axisMaxValues: function () {
      return this.levelledMaxValues(this.maxValues)
    },
    visibleAxisMaxValues: function () {
      return this.levelledMaxValues(this.visibleMaxValues)
    },
    // The same pair as an axis option. A maximum there is nothing to read — a chart whose series
    // carry no curve arrays at all — is null, which is ECharts for work it out yourself, and which
    // is the behaviour that chart had before by other means.
    axisMaxOptions: function () {
      const levelled = this.axisMaxValues
      return {
        x: isFinite(levelled.x) ? levelled.x : null,
        y: isFinite(levelled.y) ? levelled.y : null
      }
    },
    // A CHART OF ONE SERIES HAS MAXIMA TOO, and this used to hand back the NAME of that series.
    // Started without an accumulator, reduce over a one-element array returns that element without
    // calling the callback at all — so a single-curve chart computed its maxima as the string its
    // one series is keyed by, both axis maxima came out undefined, and ECharts auto-scaled: it
    // rounds a maximum UP to a round number, which is why the line ended short of the right edge,
    // and it scales the two axes independently, which is the one thing sameAxisScale exists to
    // stop. The accumulator is stated now, as it already was on the visible-series twin this is
    // otherwise identical to — and the two are one reducer rather than two spellings of it.
    maxValues: function () {
      return this.seriesMaxValues(this.channelKeys)
    },
    longestLabelLength: function () {
      var longest = this.channelKeys.reduce(
        function (a, b) {
          return a.length > b.length ? a : b
        }
      )
      return longest.length
    }
  },
  methods: {
    // The largest x and the largest y drawn across a set of series. An entry without curve arrays
    // (a channel with no data) contributes nothing, and a set where none of them do comes back
    // -Infinity, which is what every reader of these maxima tests for.
    seriesMaxValues (keys) {
      return keys.reduce((previous, key) => {
        const channelData = this.data[key]
        if (!channelData || !Array.isArray(channelData.x) || !Array.isArray(channelData.y)) {
          return previous
        }
        return {
          x: Math.max(...channelData.x, previous.x),
          y: Math.max(...channelData.y, previous.y)
        }
      }, { x: -Infinity, y: -Infinity })
    },
    // One range for two axes, or the pair untouched. Only where both maxima are real numbers: an
    // empty chart hands these reducers -Infinity, and levelling that would put a finite max on the
    // axis whose series is missing.
    levelledMaxValues (maxima) {
      if (!this.sameAxisScale || !isFinite(maxima.x) || !isFinite(maxima.y)) return maxima
      const reach = Math.max(maxima.x, maxima.y)
      return { x: reach, y: reach }
    },
    // THE POINT DRAWN AS A QUESTION. A hollow ring on a dashed grey outline with a question mark
    // inside it: the one place on the curve the reader has been asked about, marked where it falls
    // and carrying no figure, because there is no figure — the axes it would be read off are blank
    // for the same reason. It takes the series' own colour for nothing and the chart's ink for
    // everything, so it reads as a gap in the picture rather than as another measurement on it.
    unknownPoint (mark) {
      return {
        name: mark.name || '',
        symbol: 'circle',
        symbolSize: 20,
        value: '?',
        xAxis: mark.x,
        yAxis: mark.y,
        itemStyle: {
          color: '#fff',
          borderWidth: 1.5,
          borderType: 'dashed',
          borderColor: 'rgba(0, 0, 0, 0.45)'
        },
        label: {
          show: true,
          formatter: '?',
          position: 'inside',
          color: 'rgba(0, 0, 0, 0.55)',
          fontSize: 12,
          fontWeight: 'bold'
        },
        emphasis: {
          itemStyle: {
            color: '#fff',
            borderColor: 'rgba(0, 0, 0, 0.45)'
          }
        }
      }
    },
    getDisplayName (key) {
      return this.nameTranslations && key in this.nameTranslations
        ? this.nameTranslations[key]
        : key
    },
    mergeLine (lineX, lineY, datapoints) {
      // Create arrays to store merged coordinates
      const mergedX = [...lineX]
      const mergedY = [...lineY]

      // Add each datapoint to the merged arrays
      datapoints.forEach(point => {
        // Find insertion index to maintain ascending X order
        const insertIndex = mergedX.findIndex(x => x > point.x)

        // If no larger X found, append to end
        if (insertIndex === -1) {
          mergedX.push(point.x)
          mergedY.push(point.y)
        } else {
          // Insert at correct position
          mergedX.splice(insertIndex, 0, point.x)
          mergedY.splice(insertIndex, 0, point.y)
        }
      })

      // Return new line object with merged coordinates
      return {
        x: mergedX,
        y: mergedY
      }
    },
    applyVisibleAxes () {
      // Adapt the x max and the diagonal to the series currently selected in
      // the legend, so auto-scaling charts (scale) fit the visible data. This
      // must go through a targeted merged setOption instead of the reactive
      // `options` binding: vue-echarts replaces the whole option (notMerge)
      // whenever the bound object identity changes, which would reset the
      // legend selection that triggered the update in the first place.
      if (!this.scale) {
        return
      }
      const chart = this.$refs[this.chartId]
      if (!chart || !chart.setOption || !chart.getOption) {
        return
      }
      // The ECharts instance can exist before its full option is applied (with
      // autoresize, vue-echarts applies the initial option in a later nextTick).
      // Merging into an option-less chart would make ECharts treat this partial
      // update as the complete option and break the chart, so skip until then —
      // the initial option already reflects an all-selected legend. chart.chart
      // is the raw ECharts instance (undefined when not initialized/disposed,
      // in which case the getOption proxy would throw).
      if (!chart.chart || !chart.getOption()) {
        return
      }
      // No visible series carries curve arrays — nothing to adapt to
      if (!isFinite(this.visibleAxisMaxValues.x)) {
        return
      }
      const update = {}
      if (!this.xAxisTypeIsCategory) {
        update.xAxis = { max: this.visibleAxisMaxValues.x }
      }
      if (this.showDiagonal) {
        update.series = [{
          id: 'diagonal',
          data: [0, Math.min(this.visibleAxisMaxValues.x, this.visibleAxisMaxValues.y)]
        }]
      }
      if (Object.keys(update).length > 0) {
        chart.setOption(update, { notMerge: false })
      }
    },
    onLegendSelectChanged (params) {
      this.selectedSeries = params.selected
    },
    onLegendSelectAll () {
      this.selectedSeries = Object.fromEntries(
        this.channelKeys.map(key => [key, true])
      )
    },
    onLegendInverseSelect () {
      this.selectedSeries = Object.fromEntries(
        this.channelKeys.map(key => [key, this.selectedSeries[key] === false])
      )
    },
    clickedChart (params) {
      if (params.componentType === 'markPoint') {
        this.markPointDialogParams.color = params.color
        this.markPointDialogParams.value = params.value
        this.markPointDialogParams.seriesIndex = params.seriesIndex
        // this.markPointDialog = true
      }
    },
    dataArray: function (data) {
      var array = []
      for (var i in data.x) {
        array.push([
          data.x[i],
          data.y[i]
        ])
      }
      return array
    },
    formatDateWeekly (date) {
      return moment(date).format("[']YY-MMM-DD")
    },
    formatDateX (date) {
      // An explicit period (or showQuarters) is authoritative: formatDate covers all of
      // week / month / quarter / year, and the caller knows which one its dates are bucketed
      // by — the Saturation Curves period collapser, for one, can turn a weekly model's
      // points into yearly ones. The xName sniffing below is only the fallback for the
      // mounts that pass no period, and it never matched in es/pt anyway (the axis there
      // reads "Gasto Semanal", not "Weekly Spend").
      if (this.period || this.showQuarters) {
        return this.formatDate(date)
      }
      if (this.xName && this.xName.startsWith('Weekly')) {
        return this.formatDateWeekly(date)
      }
      return this.formatDate(date)
    },
    formatDate (date) {
      if (this.showQuarters) {
        const quarter = moment(date).quarter()
        const year = moment(date).format('YY')
        return `Q${quarter} '${year}`
      }
      if (this.period === 'year') {
        return moment(date).format('YYYY')
      }
      if (this.period === 'week') {
        return moment(date).format("[']YY-MMM-DD")
      }
      return moment(date).format('MMM \'YY')
    },
    formatValue: function (value) {
      // With tooltipSignificantDecimals the mantissa follows the value's own
      // magnitude, so a series that lives well below 1 (a ROAS in units per
      // currency, say) keeps two meaningful digits instead of collapsing to
      // '0.00'; without it the fixed mantissa applies, as it always has.
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: this.tooltipSignificantDecimals
          ? significantMantissa(value, this.tooltipValueMantissa)
          : this.tooltipValueMantissa,
        optionalMantissa: true,
        trimMantissa: this.tooltipSignificantDecimals
      })
    },
    dataZoomLabelFormatter: function (value) {
      if (this.xAxisTypeIsCategory && this.xAxisTypeIsDate) {
        const firstChannel = this.channelKeys && this.channelKeys[0]
        const channelData = firstChannel ? this.data[firstChannel] : null
        if (channelData && channelData.x) {
          const xValues = channelData.datapoints
            ? this.mergeLine(channelData.x, channelData.y, channelData.datapoints).x
            : channelData.x
          const index = Math.round(value)
          if (index >= 0 && index < xValues.length) {
            return this.formatDateX(xValues[index])
          }
        }
      }
      return this.formatValue(value)
    },
    formatEchartsValue: function (obj) {
      if (this.xAxisTypeIsCategory && obj.axisDimension === 'x') {
        if (this.xAxisTypeIsDate) {
          return this.formatDateX(obj.value)
        } else {
          return obj.value
        }
      } else {
        return this.formatValue(obj.value)
      }
    }
  },
  watch: {
    // Vue flushes watchers asynchronously, so the merged setOption runs after
    // ECharts has finished dispatching the legend event that changed the map
    selectedSeries: function () {
      this.applyVisibleAxes()
    },
    '$i18n.locale': {
      handler: function () {
        let options = this.options
        options.series = this.series
        if (this.$refs[this.chartId] && this.$refs[this.chartId].mergeOptions) {
          this.$refs[this.chartId].mergeOptions(options, false)
        }
      }
    },
    data: {
      immediate: true,
      handler: function () {
        let options = this.options
        options.series = this.series
        if (this.$refs[this.chartId] && this.$refs[this.chartId].mergeOptions) {
          this.$refs[this.chartId].mergeOptions(options, false)
        }
        // Uses nexTick because mergeOptions selects all series asynchronously, effective after the next code
        this.$nextTick(() => {
          // Restore the selection status
          Object.keys(this.selectedSeries).filter(key => !this.selectedSeries[key])
            .forEach(seriesName => {
              this.$refs[this.chartId].dispatchAction({
                type: 'legendUnSelect',
                name: seriesName
              })
            })
          // New data fully replaced the option with all-channels axes;
          // re-adapt them to the restored selection
          this.applyVisibleAxes()
        })
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.showChart = true
    })
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    only: {
      type: Array,
      required: false
    },
    colors: {
      type: [Object, Array],
      required: false
    },
    xName: {
      type: String,
      required: false
    },
    yName: {
      type: String,
      required: false
    },
    secondaryYName: {
      type: String,
      required: false,
      default: null
    },
    showDiagonal: {
      type: Boolean,
      required: false
    },
    showNegatives: {
      type: Boolean,
      required: false
    },
    lineStyles: {
      type: String,
      required: false
    },
    xAxisTypeIsCategory: {
      type: Boolean,
      required: false
    },
    xAxisTypeIsDate: {
      type: Boolean,
      required: false
    },
    xAxisRotate: {
      type: Number,
      required: false
    },
    gridBottomMargin: {
      type: Number,
      required: false,
      default () {
        return 30
      }
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    legendOrient: {
      type: String,
      required: false,
      default: function () {
        return 'vertical'
      }
    },
    legendBottom: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    animation: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    showLegend: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    showSelector: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    showDataZoom: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    scale: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    chartId: {
      type: String,
      required: false,
      default () {
        return `${this.$attrs.titleString}-${this._uid}`
      }
    },
    filterMode: {
      type: String,
      required: false,
      default () {
        return 'none'
      }
    },
    hideDatapoints: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    showTooltips: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    smoothLines: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    detailedTooltip: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    tooltipSignificantDecimals: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    tooltipValueMantissa: {
      type: Number,
      required: false,
      default () {
        return 1
      }
    },
    showQuarters: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    period: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    nameTranslations: {
      type: Object,
      required: false,
      default: function () {
        return {}
      }
    },
    // The series whose four region boundaries shade the plot. Needed only when a chart draws more
    // than one series OF THE SAME CHANNEL: the default rule below — shade a chart that carries a
    // single curve — cannot tell that case apart from several channels overlaid, where one channel's
    // zones painted across all of them would be read as everybody's.
    regionsChannel: {
      type: String,
      required: false,
      default: null
    },
    // BOTH AXES ON ONE RANGE, for a chart whose x and y are the same quantity. A saturation curve is
    // dollars against dollars — what a channel is given and what it gives back — and two independent
    // auto-scales lay two rulers of different lengths on one thing: a channel returning half what it
    // costs and one returning three times it draw the same shape, each filling its own box, and
    // neither picture says which it is. Given this, both axes run to the larger of the two maxima.
    // Off by default, and every existing caller is untouched: a chart of spend against share, or of
    // anything against months, has two axes that cannot be compared and must not be.
    sameAxisScale: {
      type: Boolean,
      required: false,
      default: false
    },
    // AXES WITH NO FIGURES ON THEM. A chart whose numbers do not mean anything yet — a curve
    // standing on a placeholder scale, waiting for the two figures that put it in somebody's own
    // money — is still a picture of a SHAPE, and the shape is worth drawing. What is not worth
    // drawing is the ruler beside it: a reader who takes 0.6 off an axis has read a number this
    // chart never claimed. The axis, its name and its grid stay; the values go.
    hideAxisValues: {
      type: Boolean,
      required: false,
      default: false
    },
    showDifference: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    differenceLabel: {
      type: String,
      required: false,
      default () {
        return ''
      }
    }
  },
  components: {
    'v-chart': ECharts
  }
}
</script>
